/** Pure state rules behind the AI text field's AI Write popover and insert popups. */

export type StaticAiTextActionId = 'custom-prompt' | 'rewrite' | 'shorten' | 'expand' | 'proofread';
export type AiTextActionId = StaticAiTextActionId | 'persona';

/** The bare Custom prompt bar, before anything has been generated. */
export function isCustomPromptEntryState({
  activeActionId,
  errorMessage,
  hasGeneratedResult,
  isGenerating,
}: {
  activeActionId: AiTextActionId | null;
  errorMessage: string | null;
  hasGeneratedResult: boolean;
  isGenerating: boolean;
}): boolean {
  return (
    activeActionId === 'custom-prompt' && !hasGeneratedResult && !isGenerating && !errorMessage
  );
}

/** Every result of one AI Write session, and the one on screen. */
export interface ResultHistory {
  index: number;
  items: string[];
}

export const EMPTY_RESULT_HISTORY: ResultHistory = { items: [], index: 0 };

/** Adds a result and shows it. */
export function appendResult(history: ResultHistory, text: string): ResultHistory {
  const items = [...history.items, text];
  return { items, index: items.length - 1 };
}

/** Moves the pager by `delta`, never past either end. */
export function stepResult(history: ResultHistory, delta: number): ResultHistory {
  const last = Math.max(0, history.items.length - 1);
  return { ...history, index: Math.min(last, Math.max(0, history.index + delta)) };
}

/** The result on screen, or an empty string before the first one. */
export const currentResult = (history: ResultHistory): string => history.items[history.index] ?? '';

/** A persona's instructions stay in front when the person types a refinement. */
export const combinePersonaInstruction = (persona: string, typed: string): string =>
  `${persona}\n\n${typed}`;

export const INSERT_OVER_LIMIT_TEXT = 'Not enough characters left to insert this.';

export type InsertPopup = 'dynamic-field' | 'emoji';

/** Which popup last refused an insert; `attempt` changes on every refusal so its alert remounts and is announced again. */
export type OverLimitNotice = { attempt: number; popup: InsertPopup } | null;

export type OverLimitAction = { type: 'clear' } | { type: 'refuse'; popup: InsertPopup };

export function overLimitReducer(state: OverLimitNotice, action: OverLimitAction): OverLimitNotice {
  return action.type === 'clear'
    ? null
    : { attempt: (state?.attempt ?? 0) + 1, popup: action.popup };
}
